import { Persistence } from "../../config/constants";
import { TickLayerName } from "../../config/tickLayers";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { deriveMachineCutLoad } from "../../game/cut-load";
import { emitMachineDust, machineDustMultiplier } from "../../game/Dust";
import {
  advanceOperationProgress,
  completeOperation,
  OperationCompletion,
} from "../../game/game-actions/operation-actions";
import { Machine } from "../../game/Machine";
import { operationAttendanceSatisfied } from "../../game/machine-helpers";
import { materialDustSpecies } from "../../game/material-helpers";
import { DustSpecies } from "../../game/Materials";
import { DUST_BAG_CAPTURE } from "../../game/tools/dustBag";
import { MachineEntity } from "../entities/MachineEntity";
import { operationPhasesNow } from "../machine-reads";
import { projectPerson } from "../projection";
import { DustLayer } from "../singletons/DustLayer";
import { ShopInfo } from "../singletons/ShopInfo";
import { TimeFlow } from "../TimeFlow";
import { applyCompletionGrants } from "./grants";

/**
 * The machines' slice of the engine tick. Every engine tick, each
 * in-progress operation draws down the shop minutes TimeFlow brought
 * (or waits for the player), sheds dust for the minutes it spent
 * cutting, and on completion delivers outputs and grants through the
 * shared completion commit. Attended phases only advance while the
 * player stands at the operation cell with the operate key held;
 * hands-free phases (glue curing) run regardless, even during away
 * trips. The phase arithmetic is pure (`advanceOperationProgress`), so
 * one tick can carry a cut across a phase boundary, and a forced batch
 * of minutes — a drive, the overnight — can run a whole cure in one
 * step, without this pass knowing.
 *
 * Progress within a phase is continuous state, like the clock's
 * minutes: the views poll it every frame and the DOM reads it on the
 * clock's minute, so it announces nothing. Only a phase boundary or a
 * completion is a `machineStateChanged` event.
 *
 * The system also carries the machines' half of the time model: an
 * attended, satisfied, in-progress operation is what makes machine work
 * spend the player's time (the old `machineSpendsTime`), registered with
 * TimeFlow as a spender.
 *
 * Transient — never serialized; added per session by the bootstrap.
 */
export class MachineSystem extends BaseEntity implements Entity {
  id = "machineSystem";
  tickLayer: TickLayerName = "machines";
  persistenceLevel: number = Persistence.Permanent;

  private unregisterSpender: (() => void) | null = null;

  @on("afterAdded")
  onAfterAdded() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    if (timeFlow) {
      this.unregisterSpender = timeFlow.registerSpender(() =>
        this.anyMachineSpendsTime(),
      );
    }
  }

  @on("destroy")
  onDestroy() {
    this.unregisterSpender?.();
    this.unregisterSpender = null;
  }

  @on("tick")
  onTick() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    const minutes = timeFlow?.gameDt ?? 0;
    if (minutes > 0) {
      this.pass(minutes);
    }
  }

  /**
   * Whether any machine is actively consuming the player's time this
   * tick — the old `machineSpendsTime`, over every machine.
   */
  anyMachineSpendsTime(): boolean {
    for (const entity of this.game.entities.byConstructor(MachineEntity)) {
      if (this.machineSpendsTime(entity.view())) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether this machine is actively consuming the player's time this
   * tick: an attended phase whose attendance is satisfied.
   */
  private machineSpendsTime(machine: Machine): boolean {
    const machineState = machine.state;
    if (machineState.operationProgress.status !== "inProgress") {
      return false;
    }
    const operation = machine.operations.find(
      (op) => op.id === machineState.selectedOperationId,
    );
    if (!operation) {
      return false;
    }
    if (
      !operationAttendanceSatisfied(
        machine,
        operation,
        projectPerson(this.game),
      )
    ) {
      return false;
    }
    const phases = operationPhasesNow(this.game, machine, operation);
    const { phaseIndex } = machineState.operationProgress;
    const phase = phases[Math.min(phaseIndex, phases.length - 1)];
    return phase != null && phase.attended;
  }

  /** `minutes` of shop time over every machine on the floor. Dust lands
   * after every machine has advanced, so the multipliers and phase
   * durations every machine reads are as of the step's start. */
  private pass(minutes: number): void {
    const person = projectPerson(this.game);
    const shopSize = this.game.entities.getSingleton(ShopInfo).info.size;
    const completions: OperationCompletion[] = [];
    const dustEmissions: Array<{
      machine: Machine;
      species: ReadonlyArray<DustSpecies>;
      amount: number;
    }> = [];

    for (const entity of this.game.entities.byConstructor(MachineEntity)) {
      const machineState = entity.state;
      if (machineState.operationProgress.status !== "inProgress") {
        continue;
      }

      // Look the operation up through the Machine view so mounted tools'
      // operations resolve too.
      const machine = entity.view();
      const selectedOperation = machine.operations.find(
        (op) => op.id === machineState.selectedOperationId,
      );
      if (!selectedOperation) {
        throw new Error(
          `Unknown operation: ${machineState.selectedOperationId} for machine ${machineState.machineTypeId}`,
        );
      }

      const phases = operationPhasesNow(this.game, machine, selectedOperation);
      const attended = operationAttendanceSatisfied(
        machine,
        selectedOperation,
        person,
      );
      const advance = advanceOperationProgress(
        machineState.operationProgress,
        phases,
        minutes,
        attended,
      );

      // The cut sheds sawdust for exactly the minutes it spent cutting —
      // hands-free phases (glue curing) make no mess.
      const dustOutput = selectedOperation.dustOutput ?? 0;
      if (dustOutput > 0 && advance.attendedMinutes > 0) {
        const dustMultiplier = machineDustMultiplier(
          this.game.entities.tryGetSingleton(DustLayer)?.map ?? {},
          machine,
          shopSize,
        );
        const species = [
          ...new Set(
            machineState.processingMaterials.flatMap(materialDustSpecies),
          ),
        ];
        const bagFactor = machineState.tools.includes("dustBag")
          ? 1 - DUST_BAG_CAPTURE
          : 1;
        if (species.length > 0) {
          dustEmissions.push({
            machine,
            species,
            amount:
              (dustOutput *
                bagFactor *
                deriveMachineCutLoad(machine) *
                advance.attendedMinutes) /
              dustMultiplier,
          });
        }
      }

      if (advance.completed) {
        // Through the shared finish commit, so a tick-completed cut and
        // a hand-finished pass do the same things
        const completion = completeOperation(machineState);
        completions.push(completion);
        entity.state = completion.machine;
        this.game.dispatch("machineStateChanged", { machine: entity });
        continue;
      }

      if (advance.progress !== machineState.operationProgress) {
        const phaseChanged =
          advance.progress.phaseIndex !==
          machineState.operationProgress.phaseIndex;
        entity.state = {
          ...machineState,
          operationProgress: advance.progress,
        };
        if (phaseChanged) {
          this.game.dispatch("machineStateChanged", { machine: entity });
        }
      }
    }

    // Dust lands after every machine has advanced.
    if (dustEmissions.length > 0) {
      const dustLayer = this.game.entities.tryGetSingleton(DustLayer);
      if (dustLayer) {
        let dust = dustLayer.map;
        for (const emission of dustEmissions) {
          dust = emitMachineDust(
            dust,
            emission.machine,
            emission.species,
            emission.amount,
            shopSize,
          );
        }
        dustLayer.map = dust;
      }
    }

    if (completions.length > 0) {
      applyCompletionGrants(this.game, completions);
    }
  }
}
